import type { AIResponseDTO, RealtimeMessage } from "@zplanem/responses"
import { useCallback, useEffect, useState } from "react"
import { usePropertyChannel } from "../hooks/use-property-channel"
import { getTranslations } from "../i18n"
import { useLocale } from "../locale-context"

interface Props {
	initial: AIResponseDTO | null
	propertyId: string
	/** Wrapper classes; defaults preserve the standalone "text-sm mt-2" look. */
	className?: string
}

export function AIResponseIndicator({
	initial,
	propertyId,
	className = "text-sm mt-2",
}: Props) {
	const { locale } = useLocale()
	const t = getTranslations(locale).aiIndicator
	const [latest, setLatest] = useState<AIResponseDTO | null>(initial)

	useEffect(() => {
		setLatest(initial)
	}, [initial])

	const handleMessage = useCallback((msg: RealtimeMessage) => {
		// Filter for AI response events only
		if (
			msg.event === "ai-response.created" ||
			msg.event === "ai-response.processed" ||
			msg.event === "ai-response.failed"
		) {
			setLatest(msg.payload)
		}
	}, [])
	usePropertyChannel(propertyId, handleMessage)

	if (!latest) return null
	const status = latest.failedAt
		? "failed"
		: latest.processedAt
			? "processed"
			: "pending"

	return (
		<div role="status" aria-live="polite" className={className}>
			{status === "pending" && <PendingDots base={t.processing} />}
			{status === "processed" && (
				<span className="text-green-600 dark:text-green-400">
					{t.processed}
				</span>
			)}
			{status === "failed" && (
				<span className="text-destructive">{t.failed}</span>
			)}
		</div>
	)
}

function PendingDots({ base }: { base: string }) {
	const [count, setCount] = useState(1)
	useEffect(() => {
		const id = setInterval(() => setCount((c) => (c % 3) + 1), 500)
		return () => clearInterval(id)
	}, [])
	return (
		<span className="text-warning">
			{base}
			{".".repeat(count)}
		</span>
	)
}
